import { Injectable } from '@angular/core';
import { Device } from '@ionic-native/device/ngx';
import { Network } from '@ionic-native/network/ngx';
import { Platform } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { AuthService } from '../../core/auth/auth.service';
import { CoreService } from '../../core/core/core.service';
import { AnalyticsService } from '../analytics/analytics.service';
import { CommsService } from '../comms/comms.service';

import has from 'lodash/has';

@Injectable({
  providedIn: 'root',
})
export class DiagnosticsService {
  // NETWORK
  public isOnline: boolean = true;
  public connectionType: string;
  public downlink: string;
  private _connectionTypeTimeout;

  // PING
  public serverIssue: boolean = false;
  private _pingFailedCount: number = 0;
  private _pinging: boolean = false;
  private _pingTimeout;
  private _pingSub: Subscription;

  constructor(
    public platform: Platform,
    public network: Network,
    public device: Device,
    public core: CoreService,
    public comms: CommsService,
    public analytics: AnalyticsService,
    public auth: AuthService,
  ) {
    this.platform.ready().then(() => {
      // check if page is online
      this.isOnline = navigator.onLine;

      if (this.platform.is('cordova')) {
        this.getDeviceInfo();

        this.network.onConnect().subscribe(event => {
          this.isOnline = true;

          // we need to wait briefly before we determine the connection type.
          clearTimeout(this._connectionTypeTimeout);
          this._connectionTypeTimeout = setTimeout(() => {
            this.connectionType = this.network.type;
            this.downlink = this.network.downlinkMax;
          }, 3000);
        });

        this.network.onDisconnect().subscribe(event => {
          this.isOnline = false;
          clearTimeout(this._connectionTypeTimeout);

          this.analytics.trackEvent(
            'Network Disconnected',
            this.connectionType + '-' + this.downlink,
            this.auth.Tracking_key,
            true,
          );
        });
      } else {
        if (has(navigator, 'connection')) {
          this.connectionType = navigator['connection']['effectiveType'];
          this.downlink = navigator['connection']['downlink'];
        }

        window.addEventListener('online', () => {
          this.isOnline = true;

          // we need to wait briefly before we determine the connection type.
          clearTimeout(this._connectionTypeTimeout);
          this._connectionTypeTimeout = setTimeout(() => {
            if (has(navigator, 'connection')) {
              this.connectionType = navigator['connection']['effectiveType'];
              this.downlink = navigator['connection']['downlink'];
            }
          }, 3000);
        });

        window.addEventListener('offline', () => {
          this.isOnline = false;
          clearTimeout(this._connectionTypeTimeout);

          this.analytics.trackEvent(
            'Network Disconnected',
            this.connectionType + '-' + this.downlink,
            this.auth.Tracking_key,
            true,
          );
        });
      }
    });
  }

  /**
   * Captures the extra device info such as UUID, manufacturer, model, version and platform
   */
  public getDeviceInfo(): void {
    if (this.device) {
      // uuid
      this.core.appDeviceInfo.device['uuID'] = this.device.uuid || '';

      // manufacturer
      this.core.appDeviceInfo.device['manufacturer'] = this.device.manufacturer || '';

      // model
      this.core.appDeviceInfo.device['model'] = this.device.model || '';

      // version
      this.core.appDeviceInfo.device['version'] = this.device.version || '';

      // platform
      this.core.appDeviceInfo.device['platform'] = this.device.platform || '';
    }
  }

  ping(): void {
    if (this._pinging || true) return;
    this._pinging = true;

    // now call the ping service
    // timeout after 20 seconds

    this.comms.get(
      this.core.Active_server.url + 'api/service/ping',
      {},
      success => {
        this._pingFailedCount = 0;
        this.serverIssue = false;
        this._pinging = false;
      },
      err => {
        let pingTimeout: number = 5000;

        this._pingFailedCount++;
        if (this._pingFailedCount >= 5) {
          this.serverIssue = true;
          pingTimeout = 30000;

          if (this._pingFailedCount == 5) {
            this.analytics.trackEvent('SERVER ISSUE', "Can't connect to server", this.auth.Tracking_key);
          }
        }

        // now try to ping again
        this._pingTimeout = setTimeout(() => {
          this._pinging = false;
          this.ping();
        }, pingTimeout);
      },
      30000,
    );
  }

  stopPing(): void {
    if (this._pingSub) this._pingSub.unsubscribe();

    if (this._pingTimeout) clearTimeout(this._pingTimeout);

    this.serverIssue = false;
    this._pingFailedCount = 0;
    this._pinging = false;
  }
}
